import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HighlightButton, { SecondaryButton } from '../HighlightButton/HighlightButton';
import CheckoutForm from './CheckoutForm';
import { useProducts } from '../../context/ProductsContext';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import { ANALYTICS_EVENTS, ERROR_MESSAGES } from './utils/constants';
import styles from '../../css/Store.module.css';
import ProductInfoChips from '../ProductInfoChips/ProductInfoChips';
// Icons imported in ProductInfoChips

const Store = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const { products, loading, error } = useProducts();

  // Find the current item
  const item = products.find(product => product.id === id);

  const renderPurchaseBanner = () => {
    if (!item) return null;

    return (
      <div className={styles.purchaseBanner}>
        <div className={styles.purchaseBannerButtonsContainer}>
          <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>

          <HighlightButton onClick={handlePurchaseClick}>
            Purchase for ${parseFloat(item.price).toFixed(2)}
          </HighlightButton>
        </div>
      </div>
    );
  };

  const renderYoutubeEmbed = () => {
    if (!item.youtube) return null;

    const videoId = item.youtube.includes('youtu.be/')
    ? item.youtube.split('youtu.be/')[1]
    : item.youtube.split('v=')[1]?.split('&')[0];

    const params = new URLSearchParams({
      rel: 0,              // related from same channel
      modestbranding: 1,   // smaller YouTube logo
      iv_load_policy: 3,   // hide annotations/cards
      playsinline: 1,      // inline on iOS
      fs: 0,               // hide fullscreen button
      controls: 0,      // uncomment to hide controls (you must provide your own)
      enablejsapi: 1,
      origin: window.location.origin,
    }).toString();

    return (
      <div className={styles.youtubeEmbedContainer}>
        <iframe
        className={styles.youtubeEmbed}
        width="100%"
        height="315"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?${params}`}
        title={`${item.title} - YouTube Video`}
          frameBorder="0"
          allow="encrypted-media"
          />
      </div>
    );
  }

  const handleAudioPlay = () => {
    if (analytics && item) {
      logEvent(analytics, ANALYTICS_EVENTS.AUDIO_PLAY, {
        product_id: item.id,
        product_title: item.title
      });
    }
  };

  const handlePurchaseClick = () => {
    setShowCheckout(true);
  };

  const handleBackToCatalog = () => {
    navigate('/work');
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <div style={{ paddingVertical: '2rem' }}>
            <p>An error occurred.</p>
            {/* <p>{error}</p> */}
            <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
          </div>
        </div>
      </div>
    );
  }

  // If item not found
  if (!item) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.heroSection}>
          <h1>Item Not Found</h1>
          <p>{ERROR_MESSAGES.ITEM_NOT_FOUND}</p>
          <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
        </div>
      </div>
    );
  }

  // Normal product display
  return (
    <>
      {!showCheckout && renderPurchaseBanner()}

      <div className={styles.pageContainer}>
        <div className={`${styles.mainSection} ${styles.stack}`}>
          {showCheckout && (
            <div className={styles.checkoutContainer}>
              <div className={styles.checkoutHeader}>
                <SecondaryButton onClick={() => setShowCheckout(false)}>Back To Product Info</SecondaryButton>
              </div>
              <CheckoutForm item={item} />
            </div>
          )}

          {!showCheckout && (
            <div className={styles.stack}>
            {/* Don't show title for now
            <div className={styles.heroSection}>
              <h1>{item.title}</h1>
            </div> */}

            <ProductInfoChips item={item} ensemble parts performers difficulty level />

            <div className={styles.previewSection}>
              <div className={styles.previewImageContainer}>
                <img
                  src={`${process.env.PUBLIC_URL}/static/products/${item.id}/${item.id}.jpg`}
                  alt={`Preview of ${item.title}`}
                  className={styles.previewImage}
                />
              </div>
            </div>

            <div className={styles.descriptionSection}>
              <p>{item.description}</p>
            </div>

            <ProductInfoChips item={item} numberofpages duration />

            <div>
              {item.youtube
              ? renderYoutubeEmbed()
              : <div className={styles.audioEmbed}>
                  <audio
                    className={styles.audio}
                    controls
                    controlsList="nodownload noplaybackrate nomute notimeremaining"
                    onPlay={handleAudioPlay}
                  >
                    <source src={`${process.env.PUBLIC_URL}/static/products/${item.id}/${item.id}.mp3`} type="audio/mp3" />
                  </audio>
                </div>
              }
            </div>

            <div className={styles.ctaSection}>
              <HighlightButton onClick={handlePurchaseClick}>
                <div className={styles.ctaPurchaseButton}>Purchase for ${parseFloat(item.price).toFixed(2)}</div>
              </HighlightButton>
            </div>

            <div className={styles.questionsSection}>
              <span>Questions?</span>
              <span style={{ marginRight: '3px' }}></span>
              <a href="/contact" target="_blank" rel="noopener noreferrer">
                Contact Me
              </a>
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Store;
